import {Attributes, Button, Card, Flags} from 'platform/components';
import {HStack, Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {useGetUsersQuery} from '@dms/api/customer';
import {
  TelephonyFallback,
  TelephonyLine,
  TelephonyRoutingTarget,
  useGetTelephonyGroupsQuery,
} from '@dms/api/settings';
import {UserStateFilterSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getUserName} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getTelephonyGroupSummary} from '../../TelephonyGroups/utils/getTelephonyGroupSummary';

interface TelephonyLineRoutingSummaryProps extends RequiredTestIdProps {
  line: TelephonyLine;
  onEdit: () => void;
  canEdit: boolean;
}

export function TelephonyLineRoutingSummary(props: TelephonyLineRoutingSummaryProps) {
  const routing = props.line.routing;

  const {data: groups} = useGetTelephonyGroupsQuery(undefined, {skip: routing.isDefault});
  const {data: users} = useGetUsersQuery(
    {stateFilter: UserStateFilterSchema.enum.active},
    {skip: routing.isDefault || routing.openTarget?.kind !== 'user'}
  );

  const getTargetText = (target: TelephonyRoutingTarget | Nullish) => {
    if (isNil(target)) {
      return i18n.t('page.settings.telephony.routingSummaryDefaultTarget');
    }

    return match(target.kind)
      .with('group', () => {
        const group = groups?.groups.find((item) => item.id === target.id);

        return isNil(group)
          ? i18n.t('page.settings.telephony.routingSummaryDeletedGroup')
          : `${group.name} · ${getTelephonyGroupSummary(group)}`;
      })
      .with('user', () => {
        const user = users?.find((item) => item.id === target.id);

        return getUserName(user) ?? user?.email ?? null;
      })
      .exhaustive();
  };

  const getFallbackText = (fallback: TelephonyFallback | null) =>
    match(fallback?.kind)
      .with(undefined, () => i18n.t('page.settings.telephony.routingSummaryBuiltinMessage'))
      .with('message', () => i18n.t('page.settings.telephony.routingSummaryCustomMessage'))
      .with('bot', () => i18n.t('page.settings.telephony.routingSummaryBot'))
      .exhaustive();

  return (
    <Card variant="inlineWhite" data-testid={suffixTestId('card', props)}>
      <HStack justify="space-between" align="center" spacing={2}>
        <Text size="small" alternative>
          {i18n.t('page.settings.telephony.routingSummaryTitle')}
        </Text>
        <Flags
          size="small"
          flags={[
            routing.isDefault
              ? {
                  label: i18n.t('page.settings.telephony.routingDefaultFlag'),
                  colorScheme: 'neutral',
                  isSubtle: true,
                }
              : {
                  label: i18n.t('page.settings.telephony.routingCustomFlag'),
                  colorScheme: 'blue',
                  isSubtle: true,
                },
          ]}
          data-testid={suffixTestId('flag', props)}
        />
      </HStack>
      <Attributes
        size="third"
        rows={[
          {
            label: i18n.t('page.settings.telephony.routingSummaryOpen'),
            value: getTargetText(routing.isDefault ? null : routing.openTarget),
            testId: suffixTestId('open', props),
          },
          {
            label: i18n.t('page.settings.telephony.routingSummaryClosed'),
            value: getFallbackText(routing.closedFallback),
            testId: suffixTestId('closed', props),
          },
          {
            label: i18n.t('page.settings.telephony.routingSummaryNoAnswer'),
            value: getFallbackText(routing.noAnswerFallback),
            testId: suffixTestId('noAnswer', props),
          },
        ]}
        data-testid={suffixTestId('attributes', props)}
      />
      <Show when={props.canEdit}>
        <Button
          variant="link"
          size="small"
          title={
            routing.isDefault
              ? i18n.t('page.settings.telephony.customizeRouting')
              : i18n.t('page.settings.telephony.editRouting')
          }
          onClick={props.onEdit}
          data-testid={suffixTestId('edit', props)}
        />
      </Show>
    </Card>
  );
}
